import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  cancelUSPSAMatchRegistration,
  confirmUSPSAMatchRegistration,
  createOwnerManagedUSPSAMatchRegistration,
  getUSPSAMatch,
  listAllUSPSAMatchRegistrations,
  updateUSPSAMatch,
  updateUSPSAMatchRegistration,
} from "../../lib/leaderboardApi";
import { buildAuthReturnPath, buildCurrentReturnTo } from "../../lib/authReturn";
import { getCompetitionAuthProfile } from "../../lib/competitionAuthProfile";
import { formatCalendarDate } from "../../utils/date";
import "./Leaderboard.css";

const STATUS_FILTERS = ["all", "pending", "confirmed", "cancelled", "withdrawn"];
const MATCH_STATUS_OPTIONS = ["scheduled", "in_progress", "completed"];
const PUBLICATION_STATE_OPTIONS = ["published_private", "published_public"];
const POWER_FACTOR_OPTIONS = ["major", "minor"];

function formatDate(value) {
  if (!value) return "-";
  return formatCalendarDate(value);
}

function formatStatus(value) {
  return String(value || "unknown")
    .trim()
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
}

function isAuthErrorMessage(value) {
  const message = String(value || "").toLowerCase();
  return message.includes("sign in") || message.includes("authenticated") || message.includes("user needs to be authenticated");
}

function normalizeRuleFamily(value) {
  return String(value || "competition").trim().toLowerCase();
}

function normalizeMatchLevel(value) {
  return String(value || "Level I").trim();
}

function buildLocationLabel(match) {
  const primary = String(match?.location || "").trim();
  if (primary) return primary;
  const city = String(match?.city || "").trim();
  const state = String(match?.state || "").trim();
  const range = String(match?.rangeName || "").trim();
  if (city && state) return `${city}, ${state}`;
  if (range && state) return `${range}, ${state}`;
  if (range) return range;
  if (state) return state;
  return "Location not set";
}

function buildRegistrationDraft(item = {}) {
  return {
    displayName: String(item.displayName || ""),
    email: String(item.email || ""),
    competitorNumber: String(item.competitorNumber || ""),
    uspsaMemberNumber: String(item.uspsaMemberNumber || ""),
    divisionId: String(item.divisionId || ""),
    classification: String(item.classification || ""),
    category: String(item.category || ""),
    powerFactor: String(item.powerFactor || ""),
    notes: String(item.notes || ""),
  };
}

function normalizeRegistrationDraft(draft = {}) {
  return {
    displayName: String(draft.displayName || "").trim(),
    email: String(draft.email || "").trim(),
    competitorNumber: String(draft.competitorNumber || "").trim(),
    uspsaMemberNumber: String(draft.uspsaMemberNumber || "").trim(),
    divisionId: String(draft.divisionId || "").trim(),
    classification: String(draft.classification || "").trim(),
    category: String(draft.category || "").trim(),
    powerFactor: String(draft.powerFactor || "").trim().toLowerCase(),
    notes: String(draft.notes || "").trim(),
  };
}

function registrationChanged(registration, draft) {
  const normalized = normalizeRegistrationDraft(draft);
  return (
    normalized.displayName !== String(registration?.displayName || "").trim() ||
    normalized.email !== String(registration?.email || "").trim() ||
    normalized.competitorNumber !== String(registration?.competitorNumber || "").trim() ||
    normalized.uspsaMemberNumber !== String(registration?.uspsaMemberNumber || "").trim() ||
    normalized.divisionId !== String(registration?.divisionId || "").trim() ||
    normalized.classification !== String(registration?.classification || "").trim() ||
    normalized.category !== String(registration?.category || "").trim() ||
    normalized.powerFactor !== String(registration?.powerFactor || "").trim().toLowerCase() ||
    normalized.notes !== String(registration?.notes || "").trim()
  );
}

function summaryCounts(rows) {
  return rows.reduce(
    (running, row) => {
      running.total += 1;
      const status = String(row?.status || "").trim().toLowerCase();
      if (status in running) {
        running[status] += 1;
      }
      return running;
    },
    { total: 0, pending: 0, confirmed: 0, cancelled: 0, withdrawn: 0 }
  );
}

function USPSAOwnedMatchRegistrations() {
  const location = useLocation();
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [draftById, setDraftById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionBusyKey, setActionBusyKey] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addRegistrationOpen, setAddRegistrationOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({
    status: "scheduled",
    publicationState: "published_private",
  });
  const [newRegistration, setNewRegistration] = useState(buildRegistrationDraft());
  const [showOptionalCreateFields, setShowOptionalCreateFields] = useState(false);
  const [expandedRegistrations, setExpandedRegistrations] = useState({});

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      setActionMessage("");

      try {
        const authProfile = await getCompetitionAuthProfile();
        if (!authProfile?.userId) {
          throw new Error("Missing user identity");
        }

        const [matchValue, registrationRows] = await Promise.all([
          getUSPSAMatch(matchId, { auth: true }),
          listAllUSPSAMatchRegistrations(matchId),
        ]);

        if (!active) return;

        setMatch(matchValue);
        setRegistrations(Array.isArray(registrationRows) ? registrationRows : []);
        setDraftById(
          Object.fromEntries((registrationRows || []).map((row) => [row.competitorId, buildRegistrationDraft(row)]))
        );
        setSettingsDraft({
          status: String(matchValue?.status || "scheduled"),
          publicationState: String(matchValue?.publicationState || "published_private"),
        });
      } catch (loadError) {
        if (!active) return;
        const message = String(loadError?.message || "");
        setError(isAuthErrorMessage(message) ? "Sign in to manage USPSA registrations." : (message || "Failed to load USPSA match management"));
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [matchId]);

  const filteredRegistrations = useMemo(() => {
    const query = String(searchText || "").trim().toLowerCase();
    return registrations.filter((row) => {
      const matchesStatus = statusFilter === "all" || String(row?.status || "").toLowerCase() === statusFilter;
      if (!matchesStatus) return false;
      if (!query) return true;
      return [
        row?.displayName,
        row?.email,
        row?.competitorNumber,
        row?.uspsaMemberNumber,
        row?.divisionId,
        row?.classification,
      ].some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [registrations, searchText, statusFilter]);

  const counts = useMemo(() => summaryCounts(registrations), [registrations]);
  const isSteelChallenge = normalizeRuleFamily(match?.ruleFamily) === "steel_challenge";
  const requiresMembershipId = normalizeMatchLevel(match?.level) !== "Level I";
  const normalizedNewRegistration = normalizeRegistrationDraft(newRegistration);
  const trimmedSearchText = String(searchText || "").trim();
  const hasRosterFilters = statusFilter !== "all" || Boolean(trimmedSearchText);
  const activeRosterFilterLabels = [
    statusFilter !== "all" ? formatStatus(statusFilter) : null,
    trimmedSearchText ? `Search: ${trimmedSearchText}` : null,
  ].filter(Boolean);
  const canCreateRegistration = Boolean(
    normalizedNewRegistration.displayName &&
      (!requiresMembershipId || normalizedNewRegistration.uspsaMemberNumber)
  );
  const divisionLabel = isSteelChallenge ? "Steel Challenge Division" : "Division";

  function updateRegistrationRow(nextRow) {
    setRegistrations((prev) =>
      [...prev.filter((row) => row.competitorId !== nextRow.competitorId), nextRow].sort((a, b) =>
        String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || ""))
      )
    );
  }

  function setDraftField(competitorId, field, value) {
    setDraftById((prev) => ({
      ...prev,
      [competitorId]: {
        ...(prev[competitorId] || buildRegistrationDraft()),
        [field]: value,
      },
    }));
  }

  function setExpandedRegistration(competitorId, nextValue) {
    setExpandedRegistrations((prev) => ({
      ...prev,
      [competitorId]: nextValue,
    }));
  }

  async function handleSaveMatchSettings() {
    if (!match) return;
    setActionBusyKey("settings");
    setActionMessage("");
    setError("");
    try {
      const updated = await updateUSPSAMatch(match.matchId, settingsDraft);
      setMatch(updated);
      setActionMessage("Match settings saved.");
    } catch (saveError) {
      setError(saveError?.message || "Failed to save USPSA match settings");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleCreateRegistration() {
    setActionBusyKey("create");
    setActionMessage("");
    setError("");
    try {
      const created = await createOwnerManagedUSPSAMatchRegistration(matchId, {
        ...normalizeRegistrationDraft(newRegistration),
        status: "confirmed",
      });
      setRegistrations((prev) => [created, ...prev]);
      setDraftById((prev) => ({ ...prev, [created.competitorId]: buildRegistrationDraft(created) }));
      setNewRegistration(buildRegistrationDraft());
      setAddRegistrationOpen(false);
      setActionMessage("Registration added.");
    } catch (createError) {
      setError(createError?.message || "Failed to create USPSA registration");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleSaveRegistration(competitorId) {
    const registration = registrations.find((row) => row.competitorId === competitorId);
    const registrationId = registration?.registrationId;
    if (!registrationId) {
      setError("Registration ID is missing. Refresh the roster before updating this row.");
      return;
    }
    const draft = draftById[competitorId] || buildRegistrationDraft();
    setActionBusyKey(`save:${competitorId}`);
    setActionMessage("");
    setError("");
    try {
      const updated = await updateUSPSAMatchRegistration(matchId, registrationId, normalizeRegistrationDraft(draft));
      updateRegistrationRow(updated);
      setDraftById((prev) => ({ ...prev, [competitorId]: buildRegistrationDraft(updated) }));
      setActionMessage("Registration updated.");
    } catch (saveError) {
      setError(saveError?.message || "Failed to update registration");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleConfirmRegistration(competitorId) {
    const registration = registrations.find((row) => row.competitorId === competitorId);
    const registrationId = registration?.registrationId;
    if (!registrationId) {
      setError("Registration ID is missing. Refresh the roster before confirming this row.");
      return;
    }
    setActionBusyKey(`confirm:${competitorId}`);
    setActionMessage("");
    setError("");
    try {
      const updated = await confirmUSPSAMatchRegistration(matchId, registrationId);
      updateRegistrationRow(updated);
      setActionMessage("Registration confirmed.");
    } catch (confirmError) {
      setError(confirmError?.message || "Failed to confirm registration");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleCancelRegistration(competitorId) {
    const registration = registrations.find((row) => row.competitorId === competitorId);
    const registrationId = registration?.registrationId;
    if (!registrationId) {
      setError("Registration ID is missing. Refresh the roster before cancelling this row.");
      return;
    }
    setActionBusyKey(`cancel:${competitorId}`);
    setActionMessage("");
    setError("");
    try {
      const updated = await cancelUSPSAMatchRegistration(matchId, registrationId);
      updateRegistrationRow(updated);
      setActionMessage("Registration cancelled.");
    } catch (cancelError) {
      setError(cancelError?.message || "Failed to cancel registration");
    } finally {
      setActionBusyKey("");
    }
  }

  const authGate = !loading && isAuthErrorMessage(error);
  const signInPath = buildAuthReturnPath(
    "/auth/sign-in",
    buildCurrentReturnTo(location, `/matches/manage/uspsa/${encodeURIComponent(matchId)}`)
  );

  return (
    <section className="leaderboard-page">
      <Helmet>
        <title>Registration Workbench | Range Pocket Competition</title>
      </Helmet>

      {!loading && !authGate && !error && (
      <div className="match-board-hero">
        <div>
          <div className="owner-registration-section-label">USPSA</div>
          <h1>{match?.matchName || "Manage Registrations"}</h1>
          <div className="leaderboard-meta">{match ? buildLocationLabel(match) : "Loading..."}</div>
        </div>
        <Link to={`/matches/uspsa/${encodeURIComponent(matchId)}`} className="match-search-button leaderboard-inline-link">
          Public Page
        </Link>
      </div>
      )}

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Loading</div>
          <strong>USPSA Workbench</strong>
        </div>
      )}
      {authGate && (
        <section className="leaderboard-card app-gate-panel">
          <div className="app-gate-eyebrow">Director access</div>
          <h2>Sign in to manage USPSA registrations</h2>
          <div className="registration-auth-actions app-gate-actions">
            <Link to={signInPath} className="match-search-button leaderboard-inline-link">
              Sign In
            </Link>
          </div>
        </section>
      )}
      {!loading && error && !authGate && (
        <section className="leaderboard-card app-gate-panel director-error-panel">
          <div className="app-gate-eyebrow">USPSA workbench</div>
          <h2>Registrations could not load</h2>
          <p>{error}</p>
          <div className="app-gate-actions">
            <button type="button" className="match-search-button" onClick={() => window.location.reload()}>
              Try Again
            </button>
            <Link to="/matches/manage/matches" className="registration-auth-secondary leaderboard-inline-link">
              Owned Matches
            </Link>
            <Link to="/matches/manage" className="registration-auth-secondary leaderboard-inline-link">
              Director Overview
            </Link>
          </div>
        </section>
      )}

      {!loading && !authGate && !error && (
        <>
          <div className="leaderboard-card director-registration-summary-row">
            <span><strong>{counts.total}</strong> Total</span>
            <span><strong>{counts.confirmed}</strong> Confirmed</span>
            <span><strong>{counts.pending}</strong> Pending</span>
            <span><strong>{counts.cancelled}</strong> Cancelled</span>
            <span><strong>{counts.withdrawn}</strong> Withdrawn</span>
            <span><strong>{formatStatus(match?.status)}</strong> Status</span>
          </div>

          <section className="leaderboard-card director-registration-workbench">
            <div className="director-registration-workbench-copy">
              <div>
                <div className="owner-registration-section-label">Director Workbench</div>
                <h2>Roster controls</h2>
              </div>
            </div>
            <div className="director-registration-toolbar">
              <Link to={`/matches/uspsa/${encodeURIComponent(matchId)}`} className="registration-auth-secondary leaderboard-inline-link">
                Public Page
              </Link>
              <button type="button" className="match-search-button" onClick={() => setSettingsOpen(true)}>
                Match Settings
              </button>
              <button type="button" className="match-search-button" onClick={() => setAddRegistrationOpen(true)}>
                Add Competitor
              </button>
            </div>
            {actionMessage && (
              <div className="director-registration-messages">
                <div className="owner-banner-message owner-banner-success">{actionMessage}</div>
              </div>
            )}
          </section>

          {settingsOpen && (
          <details
            className="leaderboard-card owner-registration-panel director-add-participant-details"
            open={settingsOpen}
            onToggle={(event) => setSettingsOpen(event.currentTarget.open)}
          >
            <summary>
              <span>
                <span className="owner-registration-section-label">Match</span>
                <strong>Publication and status</strong>
              </span>
              <span className="registration-auth-secondary">Open settings</span>
            </summary>
            <div className="owner-registration-create-grid">
              <label className="leaderboard-field">
                <span>Status</span>
                <select className="match-sort" value={settingsDraft.status} onChange={(event) => setSettingsDraft((prev) => ({ ...prev, status: event.target.value }))}>
                  {MATCH_STATUS_OPTIONS.map((value) => <option key={value} value={value}>{formatStatus(value)}</option>)}
                </select>
              </label>
              <label className="leaderboard-field">
                <span>Visibility</span>
                <select className="match-sort" value={settingsDraft.publicationState} onChange={(event) => setSettingsDraft((prev) => ({ ...prev, publicationState: event.target.value }))}>
                  {PUBLICATION_STATE_OPTIONS.map((value) => <option key={value} value={value}>{formatStatus(value)}</option>)}
                </select>
              </label>
              <div className="owner-registration-form-actions">
                <button type="button" className="match-search-button owner-registration-submit" onClick={handleSaveMatchSettings} disabled={actionBusyKey === "settings"}>
                  {actionBusyKey === "settings" ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </details>
          )}

          {addRegistrationOpen && (
          <details
            className="leaderboard-card owner-registration-panel director-add-participant-details"
            open={addRegistrationOpen}
            onToggle={(event) => setAddRegistrationOpen(event.currentTarget.open)}
          >
            <summary>
              <span>
                <span className="owner-registration-section-label">Registration</span>
                <strong>Add competitor</strong>
              </span>
              <span className="registration-auth-secondary">Open form</span>
            </summary>
            <div className="owner-registration-create-grid">
              <label className="leaderboard-field owner-registration-name-field"><span>Name<em className="leaderboard-required-mark">Required</em></span><input className="match-search" value={newRegistration.displayName} onChange={(event) => setNewRegistration((prev) => ({ ...prev, displayName: event.target.value }))} /></label>
              <label className="leaderboard-field"><span>{divisionLabel}</span><input className="match-search" value={newRegistration.divisionId} onChange={(event) => setNewRegistration((prev) => ({ ...prev, divisionId: event.target.value }))} /></label>
              <label className="leaderboard-field"><span>Classification</span><input className="match-search" value={newRegistration.classification} onChange={(event) => setNewRegistration((prev) => ({ ...prev, classification: event.target.value }))} /></label>
              {!isSteelChallenge && (
                <label className="leaderboard-field"><span>Power Factor</span><select className="match-sort" value={newRegistration.powerFactor} onChange={(event) => setNewRegistration((prev) => ({ ...prev, powerFactor: event.target.value }))}><option value="">Select</option>{POWER_FACTOR_OPTIONS.map((value) => <option key={value} value={value}>{formatStatus(value)}</option>)}</select></label>
              )}
              {requiresMembershipId && (
                <label className="leaderboard-field"><span>USPSA Member #<em className="leaderboard-required-mark">Required</em></span><input className="match-search" value={newRegistration.uspsaMemberNumber} onChange={(event) => setNewRegistration((prev) => ({ ...prev, uspsaMemberNumber: event.target.value }))} /></label>
              )}
            </div>
            <details
              className="owner-registration-optional-details"
              open={showOptionalCreateFields}
              onToggle={(event) => setShowOptionalCreateFields(event.currentTarget.open)}
            >
              <summary>Optional Details</summary>
              <div className="owner-registration-create-grid owner-registration-optional-grid">
                <label className="leaderboard-field"><span>Email</span><input className="match-search" value={newRegistration.email} onChange={(event) => setNewRegistration((prev) => ({ ...prev, email: event.target.value }))} /></label>
                <label className="leaderboard-field"><span>Competitor #</span><input className="match-search" value={newRegistration.competitorNumber} onChange={(event) => setNewRegistration((prev) => ({ ...prev, competitorNumber: event.target.value }))} /></label>
                {!requiresMembershipId && (
                  <label className="leaderboard-field"><span>USPSA Member #</span><input className="match-search" value={newRegistration.uspsaMemberNumber} onChange={(event) => setNewRegistration((prev) => ({ ...prev, uspsaMemberNumber: event.target.value }))} /></label>
                )}
                <label className="leaderboard-field"><span>Category</span><input className="match-search" value={newRegistration.category} onChange={(event) => setNewRegistration((prev) => ({ ...prev, category: event.target.value }))} /></label>
                <label className="leaderboard-field owner-registration-affiliations-field"><span>Notes</span><input className="match-search" value={newRegistration.notes} onChange={(event) => setNewRegistration((prev) => ({ ...prev, notes: event.target.value }))} /></label>
              </div>
            </details>
            <div className="owner-registration-form-actions">
              <button type="button" className="match-search-button owner-registration-submit" onClick={handleCreateRegistration} disabled={actionBusyKey === "create" || !canCreateRegistration}>
                {actionBusyKey === "create" ? "Adding..." : "Add"}
              </button>
            </div>
          </details>
          )}

          <div className="leaderboard-card owner-registration-panel">
            <div className="owner-registration-list-toolbar director-roster-controls">
              <div className="owner-registration-form-header">
                <div className="owner-registration-section-label">Registrations</div>
                <h2>{formatDate(match?.matchDate || match?.startDate)} · {buildLocationLabel(match)}</h2>
              </div>
              <input className="match-search" value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search registrations" />
              <label className="match-filter-field owner-registration-status-filter">
                <span>Status</span>
                <select className="match-sort" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  {STATUS_FILTERS.map((status) => (
                    <option key={status} value={status}>{status === "all" ? "All statuses" : formatStatus(status)}</option>
                  ))}
                </select>
              </label>
              {hasRosterFilters && (
                <button
                  type="button"
                  className="registration-auth-secondary leaderboard-inline-link owner-registration-clear-filter"
                  onClick={() => {
                    setSearchText("");
                    setStatusFilter("all");
                  }}
                >
                  Reset
                </button>
              )}
            </div>

            <div className="leaderboard-grid">
              {filteredRegistrations.map((registration) => {
                const draft = draftById[registration.competitorId] || buildRegistrationDraft(registration);
                const dirty = registrationChanged(registration, draft);
                const normalizedDraft = normalizeRegistrationDraft(draft);
                const canSaveRegistration = Boolean(
                  normalizedDraft.displayName &&
                    (!requiresMembershipId || normalizedDraft.uspsaMemberNumber)
                );
                const showOptionalDetails = Boolean(expandedRegistrations[registration.competitorId]);
                const statusText = String(registration.status || "unknown").toLowerCase();
                const hasCompetitorNumber = Boolean(String(registration.competitorNumber || "").trim());
                const hasCategory = Boolean(String(registration.category || "").trim());
                const hasPowerFactor = Boolean(String(registration.powerFactor || "").trim());
                const hasNotes = Boolean(String(registration.notes || "").trim());
                return (
                  <div key={registration.competitorId} className={`leaderboard-card owner-registration-card owner-registration-card-${statusText}`}>
                    <div className="owner-registration-card-header">
                      <div className="owner-registration-card-title">
                        <strong>{registration.displayName || "Competitor"}</strong>
                        {hasCompetitorNumber && (
                          <div className="owner-registration-card-subtitle">
                            <span>#{registration.competitorNumber}</span>
                          </div>
                        )}
                      </div>
                      <span className={`status-pill status-${statusText}`}>{formatStatus(registration.status)}</span>
                    </div>
                    <div className="owner-registration-summary-list">
                      {hasCompetitorNumber && (
                        <div className="owner-registration-summary-item">
                          <span>Competitor #</span>
                          <strong>#{registration.competitorNumber}</strong>
                        </div>
                      )}
                      <div className="owner-registration-summary-item">
                        <span>{divisionLabel}</span>
                        <strong>{registration.divisionId || "Not set"}</strong>
                      </div>
                      <div className="owner-registration-summary-item">
                        <span>Class</span>
                        <strong>{registration.classification || "Not set"}</strong>
                      </div>
                      <div className="owner-registration-summary-item">
                        <span>USPSA #</span>
                        <strong>{registration.uspsaMemberNumber || "Not set"}</strong>
                      </div>
                      {hasCategory && (
                        <div className="owner-registration-summary-item">
                          <span>Category</span>
                          <strong>{registration.category}</strong>
                        </div>
                      )}
                      {!isSteelChallenge && hasPowerFactor && (
                        <div className="owner-registration-summary-item">
                          <span>Power Factor</span>
                          <strong>{formatStatus(registration.powerFactor)}</strong>
                        </div>
                      )}
                      {hasNotes && (
                        <div className="owner-registration-summary-item owner-registration-summary-item-wide">
                          <span>Notes</span>
                          <strong>{registration.notes}</strong>
                        </div>
                      )}
                    </div>
                    <details className="owner-registration-edit-details">
                      <summary>
                        <span>Edit registration details</span>
                        <strong>{dirty ? "Unsaved changes" : "Open editor"}</strong>
                      </summary>
                    <div className="owner-registration-create-grid">
                      <label className="leaderboard-field owner-registration-name-field"><span>Name<em className="leaderboard-required-mark">Required</em></span><input className="match-search" value={draft.displayName} onChange={(event) => setDraftField(registration.competitorId, "displayName", event.target.value)} /></label>
                      <label className="leaderboard-field"><span>{divisionLabel}</span><input className="match-search" value={draft.divisionId} onChange={(event) => setDraftField(registration.competitorId, "divisionId", event.target.value)} /></label>
                      <label className="leaderboard-field"><span>Classification</span><input className="match-search" value={draft.classification} onChange={(event) => setDraftField(registration.competitorId, "classification", event.target.value)} /></label>
                      {!isSteelChallenge && (
                        <label className="leaderboard-field"><span>Power Factor</span><select className="match-sort" value={draft.powerFactor} onChange={(event) => setDraftField(registration.competitorId, "powerFactor", event.target.value)}><option value="">Select</option>{POWER_FACTOR_OPTIONS.map((value) => <option key={value} value={value}>{formatStatus(value)}</option>)}</select></label>
                      )}
                      {requiresMembershipId && (
                        <label className="leaderboard-field"><span>USPSA Member #<em className="leaderboard-required-mark">Required</em></span><input className="match-search" value={draft.uspsaMemberNumber} onChange={(event) => setDraftField(registration.competitorId, "uspsaMemberNumber", event.target.value)} /></label>
                      )}
                    </div>
                    <details
                      className="owner-registration-optional-details"
                      open={showOptionalDetails}
                      onToggle={(event) => setExpandedRegistration(registration.competitorId, event.currentTarget.open)}
                    >
                      <summary>Optional Details</summary>
                      <div className="owner-registration-create-grid owner-registration-optional-grid">
                        <label className="leaderboard-field"><span>Email</span><input className="match-search" value={draft.email} onChange={(event) => setDraftField(registration.competitorId, "email", event.target.value)} /></label>
                        <label className="leaderboard-field"><span>Competitor #</span><input className="match-search" value={draft.competitorNumber} onChange={(event) => setDraftField(registration.competitorId, "competitorNumber", event.target.value)} /></label>
                        {!requiresMembershipId && (
                          <label className="leaderboard-field"><span>USPSA Member #</span><input className="match-search" value={draft.uspsaMemberNumber} onChange={(event) => setDraftField(registration.competitorId, "uspsaMemberNumber", event.target.value)} /></label>
                        )}
                        <label className="leaderboard-field"><span>Category</span><input className="match-search" value={draft.category} onChange={(event) => setDraftField(registration.competitorId, "category", event.target.value)} /></label>
                        <label className="leaderboard-field owner-registration-affiliations-field"><span>Notes</span><input className="match-search" value={draft.notes} onChange={(event) => setDraftField(registration.competitorId, "notes", event.target.value)} /></label>
                      </div>
                    </details>
                    <div className="match-card-footer match-card-footer--actions">
                      <button type="button" className="detail-link detail-link-button" onClick={() => handleSaveRegistration(registration.competitorId)} disabled={!dirty || !canSaveRegistration || actionBusyKey === `save:${registration.competitorId}`}>{actionBusyKey === `save:${registration.competitorId}` ? "Saving..." : "Save"}</button>
                      <button type="button" className="detail-link detail-link-button" onClick={() => handleConfirmRegistration(registration.competitorId)} disabled={actionBusyKey === `confirm:${registration.competitorId}` || String(registration.status || "").toLowerCase() === "confirmed"}>{actionBusyKey === `confirm:${registration.competitorId}` ? "Working..." : "Confirm"}</button>
                      <button type="button" className="detail-link detail-link-button" onClick={() => handleCancelRegistration(registration.competitorId)} disabled={actionBusyKey === `cancel:${registration.competitorId}`}>{actionBusyKey === `cancel:${registration.competitorId}` ? "Working..." : "Cancel"}</button>
                    </div>
                    </details>
                  </div>
                );
              })}
              {filteredRegistrations.length === 0 && (
                <div className="leaderboard-card director-empty-state list-empty-panel">
                  <div className="owner-registration-section-label">No results</div>
                  <strong>{registrations.length === 0 ? "No registrations yet" : "No registrations found"}</strong>
                  {registrations.length === 0 ? (
                    <div className="empty-filter-summary" aria-label="Roster state">
                      <span>Roster: 0</span>
                      <span>USPSA</span>
                    </div>
                  ) : activeRosterFilterLabels.length > 0 && (
                    <div className="empty-filter-summary" aria-label="Active filters">
                      {activeRosterFilterLabels.map((label) => (
                        <span key={label}>{label}</span>
                      ))}
                    </div>
                  )}
                  <div className="director-action-row">
                    {registrations.length === 0 && (
                      <button type="button" className="match-search-button" onClick={() => setAddRegistrationOpen(true)}>
                        Add Competitor
                      </button>
                    )}
                    {registrations.length > 0 && hasRosterFilters && (
                      <button
                        type="button"
                        className="registration-auth-secondary leaderboard-inline-link detail-link-button"
                        onClick={() => {
                          setSearchText("");
                          setStatusFilter("all");
                        }}
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export const USPSAOwnedMatchRegistrations_Page = {
  path: "/matches/manage/uspsa/:matchId",
  element: <USPSAOwnedMatchRegistrations />,
};
